import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <place-detail> custom element used by the template.
import "../../components/place/detail";

import template from "./GastroListTemplate.html";
import { _lcs } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// The labels and titles below are German source strings that double as
// dictionary keys — module constants are evaluated before I18n exists, so the
// translation happens where they are read.
const STATUS_LABELS = {
    published : "Veröffentlicht",
    draft     : "Entwurf",
    cancelled : "Abgesagt",
    postponed : "Verschoben",
    archived  : "Archiviert"
};

/**
 * The category vocabulary of a gastronomy place.
 *
 * Held here rather than fetched: unlike Event.CATEGORY the place categories
 * carry no CHECK constraint and no catalog endpoint — importers map provider
 * vocabularies into the column freely (see model/Place). This is the curated
 * set the console offers as filters; a record may carry others, which is why
 * an unknown key filters to an empty result rather than an error.
 */
const CATEGORIES = [
    { key: "restaurant",   title: "Restaurant",        icon: "utensils" },
    { key: "cafe",         title: "Café",              icon: "coffee" },
    { key: "bar",          title: "Bar",               icon: "wine" },
    { key: "bakery",       title: "Bäckerei",          icon: "croissant" },
    { key: "winery",       title: "Weingut",           icon: "wine" },
    { key: "brewery",      title: "Brauerei",          icon: "flame" },
    { key: "beer_garden",  title: "Biergarten",        icon: "leaf" },
    { key: "mountain_hut", title: "Berghütte",         icon: "mountain" },
    { key: "ice_cream",    title: "Eisdiele",          icon: "snowflake" },
    { key: "farm_shop",    title: "Hofladen",          icon: "leaf" }
];

const CATEGORY_TITLES = CATEGORIES.reduce( ( map, category ) =>
{
    map[ category.key ] = category.title;
    return map;
}, {} );

/**
 * GastroList — "Gastronomie": the platform's own pool of restaurants, cafés,
 * bars and breweries (GET /place/platform?type=gastro).
 *
 * The sibling of PlatformEventList and deliberately the same shape — same
 * toolbar, same GridJS table, same row markers — minus the two filters that
 * only mean something for a record with a schedule (Zeitraum, "Vergangene
 * einschließen"). A restaurant is always on.
 *
 * Rows are the compact admin payload; the full record is fetched when a row is
 * opened, because a table cell cannot carry opening hours, a description and
 * six pictures.
 */
export class GastroList extends InAppState
{
    static ROUTE = '/gastronomy';
    static ID = 'gastro-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="places-state"]' );
        this.truncatedEl = root.querySelector( '[data-id="truncated"]' );
        this.sourceFilter = root.querySelector( '[data-id="filter-source"]' );
        this.statusFilter = root.querySelector( '[data-id="filter-status"]' );
        this.duplicateFilter = root.querySelector( '[data-id="filter-duplicates"]' );
        this.detail = root.querySelector( '[data-id="place-detail"]' );

        this.categoryToggle = root.querySelector( '[data-id="filter-category-toggle"]' );
        this.categoryPanel = root.querySelector( '[data-id="filter-category-panel"]' );
        this.categoryList = root.querySelector( '[data-id="filter-category-list"]' );
        this.categoryLabel = root.querySelector( '[data-id="filter-category-label"]' );

        this.places = [];
        this.placeById = new Map();
        this.sources = [];
        this.selectedCategories = new Set();
        this.grid = null;

        // Handed over by the source list ("Gastronomie" on a source card).
        this.selectedSourceId = this.app.pendingPlaceSourceId || "";
        this.app.pendingPlaceSourceId = null;

        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );
        this.addEventListener( this.detail, "place-saved", () => this.loadPlaces() );
        this.addEventListener( this.sourceFilter, "change", () => this.loadPlaces() );
        this.addEventListener( this.statusFilter, "change", () => this.loadPlaces() );
        this.addEventListener( this.duplicateFilter, "change", () => this.loadPlaces() );
        this.addEventListener( root.querySelector( '[data-id="btn-reload"]' ), "click", () => this.loadPlaces() );

        this.bindCategoryFilter();
        this.renderCategoryFilter();

        await this.loadSources();
        await this.loadPlaces();
    }

    /* --------------------------- Category multi-select ------------------------ */

    bindCategoryFilter()
    {
        this.addEventListener( this.categoryToggle, "click", ( e ) =>
        {
            e.stopPropagation();
            this.toggleCategoryPanel( this.categoryPanel.hidden );
        } );

        // Checking a box filters immediately; the panel stays open so several
        // categories can be picked in one go.
        this.addEventListener( this.categoryList, "change", ( e ) =>
        {
            const input = e.target.closest( "input[type=checkbox]" );
            if ( !input ) return;

            if ( input.checked ) this.selectedCategories.add( input.value );
            else this.selectedCategories.delete( input.value );

            this.updateCategoryLabel();
            this.loadPlaces();
        } );

        this.addEventListener( this.categoryPanel.querySelector( '[data-id="filter-category-clear"]' ), "click", () =>
        {
            this.selectedCategories.clear();
            this.categoryList.querySelectorAll( "input[type=checkbox]" ).forEach( ( input ) => { input.checked = false; } );
            this.updateCategoryLabel();
            this.loadPlaces();
        } );

        // Tracked, so InfrontJS removes them when the state exits.
        this.addEventListener( document, "click", ( e ) =>
        {
            if ( !this.categoryPanel.hidden && !this.categoryPanel.contains( e.target ) )
            {
                this.toggleCategoryPanel( false );
            }
        } );
        this.addEventListener( document, "keydown", ( e ) =>
        {
            if ( e.key === "Escape" ) this.toggleCategoryPanel( false );
        } );
    }

    toggleCategoryPanel( open )
    {
        this.categoryPanel.hidden = !open;
        this.categoryToggle.setAttribute( "aria-expanded", String( open ) );
    }

    renderCategoryFilter()
    {
        this.categoryList.innerHTML = CATEGORIES.map( ( category ) =>
            '<label class="checkbox evp__multi-item">' +
                '<input type="checkbox" value="' + this._escape( category.key ) + '" />' +
                '<span class="checkbox__box"><i class="icon icon--check"></i></span>' +
                '<i class="icon icon--' + this._escape( category.icon ) + ' evp__multi-icon"></i>' +
                this._escape( _lcs( category.title ) ) +
            '</label>'
        ).join( "" );

        this.updateCategoryLabel();
    }

    updateCategoryLabel()
    {
        const count = this.selectedCategories.size;

        if ( count === 0 )
        {
            this.categoryLabel.textContent = _lcs( "Alle Kategorien" );
        }
        else if ( count === 1 )
        {
            const key = Array.from( this.selectedCategories )[ 0 ];
            this.categoryLabel.textContent = CATEGORY_TITLES[ key ] ? _lcs( CATEGORY_TITLES[ key ] ) : key;
        }
        else
        {
            this.categoryLabel.textContent = _lcs( "{count} Kategorien", { count } );
        }

        this.categoryToggle.classList.toggle( "evp__multi-toggle--active", count > 0 );
    }

    /* ------------------------------- Loading -------------------------------- */

    /**
     * Only gastronomy feeds go in the filter. Best-effort: the filter is a
     * convenience, an unreachable source list must not stop the places from
     * loading.
     */
    async loadSources()
    {
        try
        {
            const { result } = await this.app.api.get( "/eventsource" );
            const all = ( result && +result.status === 200 && Array.isArray( result.data ) ) ? result.data : [];
            this.sources = all.filter( ( source ) => source.contentType === "gastro" );
        }
        catch ( e )
        {
            this.sources = [];
        }

        this.sourceFilter.innerHTML = '<option value="">' + this._escape( _lcs( "Alle Quellen" ) ) + '</option>' +
            this.sources.map( ( source ) =>
                '<option value="' + this._escape( source.id ) + '">' + this._escape( source.name || source.key ) + '</option>'
            ).join( "" );

        this.sourceFilter.value = this.selectedSourceId || "";
    }

    async loadPlaces()
    {
        this.showMessage( "utensils", _lcs( "Lade Gastronomie …" ), _lcs( "Einen Moment bitte." ) );
        this.truncatedEl.hidden = true;

        const query = [ "type=gastro" ];
        if ( this.sourceFilter.value ) query.push( "source=" + encodeURIComponent( this.sourceFilter.value ) );
        if ( this.statusFilter.value ) query.push( "status=" + encodeURIComponent( this.statusFilter.value ) );
        if ( this.selectedCategories.size > 0 )
        {
            query.push( "category=" + encodeURIComponent( Array.from( this.selectedCategories ).join( "," ) ) );
        }
        if ( this.duplicateFilter.value !== "exclude" ) query.push( "duplicates=" + encodeURIComponent( this.duplicateFilter.value ) );

        let payload = null;
        try
        {
            const { result } = await this.app.api.get( "/place/platform?" + query.join( "&" ) );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected /place/platform response." );
            }
            payload = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Gastronomie konnte nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.places = Array.isArray( payload.items ) ? payload.items : [];
        this.placeById = new Map();
        this.places.forEach( ( place ) => this.placeById.set( String( place.id ), place ) );

        // Never pretend a capped list is the whole list.
        if ( payload.isTruncated )
        {
            this.truncatedEl.textContent = _lcs(
                "Es werden die ersten {limit} Einträge angezeigt. Grenze die Auswahl über die Filter weiter ein.",
                { limit: payload.limit }
            );
            this.truncatedEl.hidden = false;
        }

        if ( this.places.length === 0 )
        {
            this.showMessage(
                "utensils",
                _lcs( "Keine Gastronomie gefunden" ),
                _lcs( "Für diese Auswahl liegen keine Daten vor. Der Import läuft über „Datenquellen“." )
            );
            return;
        }

        this.renderGrid();
    }

    renderGrid()
    {
        this._destroyGrid();
        this.stateEl.innerHTML = "";

        const rows = this.places.map( ( place ) => [
            place.name || "—",
            this._categoryText( place ),
            place.city || "—",
            this._priceText( place ),
            this._cuisineText( place ),
            this._sourceLabel( place ),
            String( place.status || "" ),
            String( place.id )
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Name" ),
                    formatter: ( cell, row ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._markersHtml( row.cells[ 7 ] ? this.placeById.get( String( row.cells[ 7 ].data ) ) : null ) +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                _lcs( "Kategorie" ),
                _lcs( "Ort" ),
                {
                    name: _lcs( "Preis" ),
                    formatter: ( cell ) => html(
                        '<span class="plc__price" title="' + this._escape( _lcs( "Preisniveau" ) ) + '">' +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Küche" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' + this._escape( cell ) + '</span>'
                    )
                },
                _lcs( "Quelle" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html(
                        '<span class="acc-status acc-status--' + this._escape( String( cell ).toLowerCase() ) + '">' +
                            this._escape( STATUS_LABELS[ cell ] ? _lcs( STATUS_LABELS[ cell ] ) : ( cell || "—" ) ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) =>
                    {
                        const details = this._escape( _lcs( "Details" ) );
                        return html(
                            '<div class="admin-users__actions">' +
                                '<button type="button" class="admin-users__action" data-action="open" ' +
                                        'data-place-id="' + this._escape( cell ) + '" title="' + details + '" aria-label="' + details + '">' +
                                    '<i class="icon icon--eye"></i><span>' + details + '</span>' +
                                '</button>' +
                            '</div>'
                        );
                    }
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 25, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Einträge" ), _lcs( "Keine Gastronomie gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    /**
     * Row markers: edited-by-hand (immune to the import), recognised-as-a-copy
     * (hidden from guests) and closed for good. All three change what the
     * record means, so they belong next to the name rather than in a column
     * nobody scans.
     */
    _markersHtml( place )
    {
        if ( !place )
        {
            return "";
        }

        let markers = "";
        if ( place.isLocked )
        {
            markers += '<i class="icon icon--lock evp__lock" title="' +
                this._escape( _lcs( "Manuell bearbeitet — wird beim Import nicht überschrieben" ) ) + '"></i> ';
        }
        if ( place.isDuplicate )
        {
            markers += '<i class="icon icon--layers evp__dupe" title="' +
                this._escape( _lcs( "Dublette — dieser Eintrag kam bereits über eine andere Quelle und wird Gästen nicht angezeigt" ) ) + '"></i> ';
        }
        if ( place.isPermanentlyClosed )
        {
            markers += '<i class="icon icon--x plc__closed" title="' +
                this._escape( _lcs( "Dauerhaft geschlossen" ) ) + '"></i> ';
        }
        return markers;
    }

    /* -------------------------------- Actions -------------------------------- */

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        if ( btn.getAttribute( "data-action" ) === "open" )
        {
            this.openDetail( btn.getAttribute( "data-place-id" ) );
        }
    }

    /**
     * The table holds compact rows; the panel needs the full record (long
     * text, opening hours, images, contact), so it is fetched on demand.
     */
    async openDetail( placeId )
    {
        try
        {
            const { result } = await this.app.api.get( "/place/platform/" + placeId );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected place response." );
            }
            this.detail.open( result.data, this._sourceName( result.data.contentSourceId ) );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Eintrag konnte nicht geladen werden." ), "error" );
        }
    }

    /* ------------------------------- Formatting ------------------------------ */

    _categoryText( place )
    {
        const categories = Array.isArray( place.categories ) ? place.categories : [];
        if ( categories.length === 0 )
        {
            return "—";
        }
        return categories.map( ( key ) => CATEGORY_TITLES[ key ] ? _lcs( CATEGORY_TITLES[ key ] ) : key ).join( ", " );
    }

    /** "€€" reads faster than "2" for a price level, and sorts the same. */
    _priceText( place )
    {
        const level = +place.priceLevel;
        return Number.isFinite( level ) && level >= 1 && level <= 4 ? "€".repeat( level ) : "—";
    }

    _cuisineText( place )
    {
        const cuisines = Array.isArray( place.cuisines ) ? place.cuisines : [];
        return cuisines.length > 0 ? cuisines.join( ", " ) : "—";
    }

    _sourceLabel( place )
    {
        if ( place.contentSourceId )
        {
            return this._sourceName( place.contentSourceId );
        }
        return place.source === "platform" ? "BELTURO" : ( place.sourceProvider || "—" );
    }

    _sourceName( contentSourceId )
    {
        const source = this.sources.find( ( s ) => String( s.id ) === String( contentSourceId ) );
        return source ? ( source.name || source.key ) : "—";
    }

    showMessage( icon, title, text )
    {
        this._destroyGrid();
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _destroyGrid()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is replaced anyway */ }
            this.grid = null;
        }
    }

    async exit()
    {
        this._destroyGrid();
        await super.exit();
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Gastronomie" ),
            subtitle: _lcs( "Restaurants, Cafés und Bars der Region" )
        };
    }
}
